import { ExternalLinkIcon, RefreshIcon, SearchIcon } from "@heroicons/react/outline";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { OAuthError } from "../../backend";
import useDebounce from "../../hooks/useDebounce";
import useHubspotSearch from "../../hooks/useHubspotSearch";
import { HubspotObject } from "../../interfaces";
import SmallTextInputField from "../SmallTextInputField";

interface Props {
  portalId?: string;
  selectedDealId?: string;
  onSelect: (dealId: string, dealName: string) => void;
  onReconnect: (onConnected: () => void) => void;
}

function dealName(deal: HubspotObject): string {
  return deal.properties?.dealname || deal.id;
}

function OnboardingDealsCard({ portalId, selectedDealId, onSelect, onReconnect }: Props) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 400);

  const isSearching = debouncedSearch.trim().length > 0;

  const { data, error, isValidating, mutate } = useHubspotSearch({
    primaryObjectTypeId: "0-3", // deals
    search: debouncedSearch,
    properties: ["dealname", "amount"],
    limit: isSearching ? undefined : 50,
    sortingField: isSearching ? undefined : "hs_lastmodifieddate",
    sortingDirection: "DSC",
  });

  const deals = data?.results ?? [];
  const needsReconnect = error instanceof OAuthError;
  const errorMessage = needsReconnect
    ? t("HubSpot access expired. Reconnect to load your deals.")
    : error ? (error.message || t("Couldn't load HubSpot deals.")) : null;

  return (
    <div className="flex flex-col w-full border border-gray-200 rounded-lg overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-200">
        <div className="flex items-center gap-3 min-w-0">
          <img src={`${process.env.REACT_APP_STATIC_URL}/hubspot-icon.svg`} alt="" className="h-8 w-8 flex-shrink-0" />
          <div className="min-w-0">
            <div className="font-gilroy font-semibold text-gray-600 flex items-center gap-2">
              HubSpot
              {needsReconnect
                ? <span className="text-yellow text-sm">{t("Reconnect needed")}</span>
                : <span className="text-green text-sm">{t("Connected")}</span>}
            </div>
            {portalId && (
              <a
                className="block font-gilroy font-semibold text-gray-400 text-xs truncate hover:underline"
                href={`https://app.hubspot.com/reports-dashboard/${portalId}`}
                target="_blank"
                rel="noreferrer"
              >
                {t("Portal")} {portalId}
              </a>
            )}
          </div>
        </div>
        <button
          type="button"
          className={`btn text-sm py-1 px-4 flex-shrink-0 ${needsReconnect ? "btn-blue" : "btn-white"}`}
          disabled={isValidating}
          onClick={() => onReconnect(() => mutate())}
        >
          {t("Reconnect")}
        </button>
      </div>

      {!needsReconnect && (
        <>
          <div className="flex items-center gap-3 px-4 py-2.5 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center gap-1.5 font-gilroy font-semibold text-gray-600 text-sm flex-shrink-0">
              {t("Your deals ({{count}})", { count: data?.total ?? deals.length })}
              <button
                type="button"
                className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50"
                disabled={isValidating}
                onClick={() => mutate()}
                title={t("Refresh")}
              >
                <RefreshIcon className={`w-4 h-4 ${isValidating ? "animate-spin" : ""}`} />
              </button>
            </div>
            <div className="relative flex-1 min-w-0 max-w-xs ml-auto">
              <SearchIcon className="w-4 h-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none z-10" />
              <SmallTextInputField
                value={search}
                onChange={setSearch}
                placeholder={t("Search deals...")}
                className="w-full"
                inputClassName="pl-8 h-8 text-sm"
                hidePrompt
              />
            </div>
          </div>

          <div className="flex items-center justify-between gap-3 px-4 py-2 border-b border-gray-200 bg-gray-100 font-gilroy font-semibold text-xs text-gray-500">
            <span>{t("Deal Name")}</span>
            <span>{t("Value")}</span>
          </div>

          <div className="flex flex-col w-full overflow-y-auto max-h-[40vh]">
            {deals.map((deal) => (
              <div
                key={deal.id}
                className={`flex items-center justify-between gap-3 px-4 py-2.5 border-b border-gray-100 last:border-0 cursor-pointer ${selectedDealId === deal.id ? "bg-blue-50" : "hover:bg-gray-100"}`}
                onClick={() => onSelect(deal.id, dealName(deal))}
              >
                <a
                  className="flex items-center gap-1 font-gilroy font-semibold text-sm text-gray-600 hover:underline min-w-0"
                  href={portalId ? `https://app.hubspot.com/contacts/${portalId}/record/0-3/${deal.id}/` : undefined}
                  target="_blank"
                  rel="noreferrer"
                  onClick={(e) => e.stopPropagation()}
                >
                  <span className="truncate">{dealName(deal)}</span>
                  {portalId && <ExternalLinkIcon className="w-4 h-4 flex-shrink-0" />}
                </a>
                <span className="font-gilroy font-semibold text-xs text-gray-400 flex-shrink-0">
                  {deal.properties?.amount}
                </span>
              </div>
            ))}
            {!isValidating && deals.length === 0 && !errorMessage && (
              <div className="px-4 py-8 text-center text-gray-400 font-semibold text-sm">
                {search ? t("No deals match your search.") : t("No deals found. You can use sample data instead.")}
              </div>
            )}
          </div>
        </>
      )}

      {errorMessage && (
        <div className={`px-4 py-6 text-center font-semibold text-sm ${needsReconnect ? "text-yellow" : "text-gray-600"}`}>
          {errorMessage}
        </div>
      )}
    </div>
  );
}

export default OnboardingDealsCard;
